import { Component, OnInit, Output, EventEmitter, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { IPlacesAutocompleteOutput } from '@app/shared/components/places-autocomplete/places-autocomplete-action-output.interface';
import { PlacesAutocompleteActionEnum } from '@app/shared/components/places-autocomplete/places-autocomplete-action.enum';
import { IPlacesAutocompleteResult } from '@app/shared/models/places-autocomplete-result.model';
import { PlacesAutocompleteService } from '@app/shared/services/places-autocomplete/places-autocomplete.service';
import { Subject, Subscription, debounceTime, filter, switchMap } from 'rxjs';

@Component({
  selector: 'app-places-autocomplete',
  templateUrl: './places-autocomplete.component.html',
  styleUrls: ['./places-autocomplete.component.scss']
})
export class PlacesAutocompleteComponent implements OnInit, OnDestroy {

  @Output() selectedPlace = new EventEmitter<IPlacesAutocompleteOutput>();
  results: IPlacesAutocompleteResult[];
  query$ = new Subject<string>();
  placeInputValue: string;
  masterSubscription = new Subscription();
  activeResultsItemIndex = 0;
  isPlaceInputFocused: boolean;
  isMouseHoveringResults: boolean;

  constructor(
    private placesAutocompleteService: PlacesAutocompleteService,
    private changeDetector: ChangeDetectorRef,
    private ngZone: NgZone) { }

  ngOnInit(): void {
    this.handleQuerying();
    this.resetResults();
  }

  ngOnDestroy(): void {
    this.masterSubscription.unsubscribe();
  }

  onPlaceInputKeyup($event: KeyboardEvent): void {
    const query = ($event.target as HTMLInputElement).value;
    this.query$.next(query);
  }

  onPlaceInputKeydown($event: KeyboardEvent): void {
    switch ($event.key) {
      case 'ArrowUp':
        $event.preventDefault();
        this.prevActiveMatch();
        break;
      case 'ArrowDown':
        $event.preventDefault();
        this.nextActiveMatch();
        break;
      case 'Enter':
        this.enterPressed();
        break;
    }
  }

  onPlaceInputFocus($event: FocusEvent): void {
    this.isPlaceInputFocused = true;
    const query = ($event.target as HTMLInputElement).value;
    this.query$.next(query);
    this.resetResults();
    this.resetActiveIndex();
  }

  onPlaceInputBlur(): void {
    this.isPlaceInputFocused = false;
    if (!this.placeInputValue) {
      return;
    }

    this.emitSelectedValue({
      selectedPlace: this.placeInputValue,
      actionPerformed: PlacesAutocompleteActionEnum.InputBlurred
    });
  }

  onResultsMouseEnter(): void {
    this.isMouseHoveringResults = true;
  }

  onResultsMouseLeave(): void {
    this.isMouseHoveringResults = false;
  }

  shouldShowResults(): boolean {
    return (this.results && this.results.length && (this.isPlaceInputFocused || this.isMouseHoveringResults));
  }

  placeResultClicked(place: IPlacesAutocompleteResult): void {
    this.emitSelectedValue({
      selectedPlace: place.city,
      actionPerformed: PlacesAutocompleteActionEnum.MouseClicked
    });
  }

  private enterPressed(): void {
    if (!this.results[this.activeResultsItemIndex]) {
      return;
    }

    this.emitSelectedValue({
      selectedPlace: this.results[this.activeResultsItemIndex].city,
      actionPerformed: PlacesAutocompleteActionEnum.EnterPressed
    });
  }

  private handleQuerying(): void {
    const sub = this.query$
      .pipe(
        debounceTime(200),
        filter(term => term !== ''),
        filter(term => term.replace(/\s/g, '').length !== 0),
        switchMap(term => this.placesAutocompleteService.getPlacePredictions(term)))
      .subscribe((results: IPlacesAutocompleteResult[]) => {
        this.results = results;
        this.changeDetector.detectChanges();
      });

    this.masterSubscription.add(sub);
  }

  private resetActiveIndex() {
    this.activeResultsItemIndex = 0;
  }

  private resetResults(): void {
    this.results = [];
  }

  private nextActiveMatch(): void {
    this.activeResultsItemIndex = (this.activeResultsItemIndex + 1) % this.results.length;
  }

  private prevActiveMatch(): void {
    const mod = (division, divisor) => {
      const remain = division % divisor;
      return Math.floor(remain >= 0 ? remain : remain + divisor);
    };
    this.activeResultsItemIndex = mod(this.activeResultsItemIndex - 1, this.results.length);
  }

  private emitSelectedValue(object: IPlacesAutocompleteOutput) {
    this.ngZone.run(() => {
      this.selectedPlace.emit(object);
    });
  }
}
